import type { ReactNode } from 'react';
import type { ContextTable } from '../../service/types';

type ContextTableRow = Record<string, string | number | boolean | null | undefined>;

type Props = {
  tables?: ContextTable[];
  tableIds?: string[];
  showSectionBreak?: boolean;
  renderRowAction?: (table: ContextTable, row: ContextTableRow, rowIndex: number) => ReactNode;
};

const REWARD_TOTAL_WARNING_THRESHOLD = 1800;

const parseCurrency = (value: unknown): number => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const cleaned = String(value || '').replace(/[^0-9.-]/g, '');
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : 0;
};

const shouldWarnRewardTotalCell = (
  table: ContextTable,
  row: ContextTableRow,
  columnKey: string,
) => {
  if (table.id !== 'reward-totals') return false;

  const approvedTotal = parseCurrency(row.approvedTotal);
  const pendingTotal = parseCurrency(row.pendingTotal);

  if (columnKey === 'approvedTotal') {
    return approvedTotal >= REWARD_TOTAL_WARNING_THRESHOLD;
  }
  if (columnKey === 'pendingTotal') {
    return approvedTotal + pendingTotal >= REWARD_TOTAL_WARNING_THRESHOLD;
  }
  return false;
};

const hasRewardTotalsWarning = (table: ContextTable) =>
  table.id === 'reward-totals' &&
  table.rows.some(
    (row) =>
      shouldWarnRewardTotalCell(table, row, 'approvedTotal') ||
      shouldWarnRewardTotalCell(table, row, 'pendingTotal'),
  );

const renderContextTableCellValue = (
  row: ContextTableRow,
  columnKey: string,
) => {
  const value = String(row[columnKey] ?? '');
  const statusEnteredAt = String(row.statusEnteredAt ?? '').trim();

  if (columnKey === 'status' && statusEnteredAt) {
    return (
      <>
        <span>{statusEnteredAt}</span>
        <br />
        <span>{value}</span>
      </>
    );
  }

  return value;
};

const ContextTables = ({
  tables = [],
  tableIds = [],
  showSectionBreak = false,
  renderRowAction,
}: Props) => {
  const allowedTableIds = new Set(tableIds);
  const visibleTables = tables.filter((table) => allowedTableIds.has(table.id));

  if (visibleTables.length === 0) return null;

  return (
    <div className="govuk-!-margin-top-6">
      {visibleTables.map((table) => {
        const rowActions = table.rows.map((row, index) => renderRowAction?.(table, row, index));
        const hasRowActions = rowActions.some(Boolean);

        return (
          <div className="govuk-!-margin-bottom-6" key={table.id}>
            <h2 className="govuk-heading-l">{table.title}</h2>
            <table className="govuk-table">
              <thead className="govuk-table__head">
                <tr className="govuk-table__row">
                  {table.columns.map((column) => (
                    <th className="govuk-table__header" scope="col" key={column.key}>
                      {column.label}
                    </th>
                  ))}
                  {hasRowActions && (
                    <th className="govuk-table__header" scope="col">
                      Action
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="govuk-table__body">
                {table.rows.length > 0 ? (
                  table.rows.map((row, index) => (
                    <tr className="govuk-table__row" key={`${table.id}-${index}`}>
                      {table.columns.map((column) => {
                        const warnCell = shouldWarnRewardTotalCell(table, row, column.key);
                        return (
                          <td
                            className={`govuk-table__cell${warnCell ? ' govuk-!-font-weight-bold' : ''}`}
                            key={column.key}
                            style={warnCell ? { color: '#d4351c' } : undefined}
                          >
                            {renderContextTableCellValue(row, column.key)}
                          </td>
                        );
                      })}
                      {hasRowActions && (
                        <td className="govuk-table__cell">
                          {rowActions[index] || null}
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr className="govuk-table__row">
                    <td
                      className="govuk-table__cell"
                      colSpan={table.columns.length + (hasRowActions ? 1 : 0)}
                    >
                      None
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            {hasRewardTotalsWarning(table) && (
              <p className="govuk-error-message">
                Employees may receive more than one in-year reward during the financial year, but
                the total value of all in-year rewards received during this period should not exceed
                £2000. This does not include Long Term Service Awards.
              </p>
            )}
          </div>
        );
      })}
      {showSectionBreak && (
        <hr className="govuk-section-break govuk-section-break--l govuk-section-break--visible" />
      )}
    </div>
  );
};

export default ContextTables;
